/**
 * ClientClarificationPortal - Magic Link Transaction Categorization
 * 
 * Public page where clients can categorize flagged transactions.
 * Accessible via magic link token - no authentication required.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  HelpCircle,
  Send,
  Info,
  Clock,
  Shield,
  Check,
  Copy,
  Layers
} from 'lucide-react';
import api from '../utils/api';
import { FinancialValue } from '../components/shared/FinancialValue';

interface Category {
  value: string;
  label: string;
  type: string;
}

interface Transaction {
  transaction_id: string;
  date: string;
  description: string;
  amount: number;
  current_category?: string;
  question: string;
  options?: string[];
  client_category?: string;
  client_notes?: string;
}

interface ClarificationData {
  status: string;
  request_id: string;
  claimant_name: string;
  transactions: Transaction[];
  transaction_count: number;
  categories: Category[];
  can_resubmit: boolean;
  submitted_at?: string;
  message?: string;
  completed_at?: string;
}

interface TransactionResponse {
  transaction_id: string;
  selected_category: string;
  new_category_name?: string;
  notes?: string;
}

// Helper to find similar transactions
const findSimilarTransactions = (txn: Transaction, allTransactions: Transaction[]): Transaction[] => {
  const description = txn.description.toLowerCase();
  
  // Extract key words (skip common words)
  const skipWords = new Set(['the', 'and', 'for', 'inc', 'llc', 'ltd', 'co', 'corp', 'payment', 'transfer']);
  const keyWords = description
    .split(/[\s\-_\/\\]+/)
    .filter(word => word.length > 2 && !skipWords.has(word))
    .slice(0, 3);
  
  if (keyWords.length === 0) return [];
  
  return allTransactions.filter(t => {
    if (t.transaction_id === txn.transaction_id) return false;
    const otherDesc = t.description.toLowerCase();
    // Match if any key word is found
    return keyWords.some(word => otherDesc.includes(word));
  });
};

export default function ClientClarificationPortal() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ClarificationData | null>(null);
  const [responses, setResponses] = useState<Record<string, TransactionResponse>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [bulkApplyModal, setBulkApplyModal] = useState<{
    isOpen: boolean;
    sourceTransaction: Transaction | null;
    similarTransactions: Transaction[];
    selectedCategory: string;
  }>({ isOpen: false, sourceTransaction: null, similarTransactions: [], selectedCategory: '' });
  const [bulkApplySuccess, setBulkApplySuccess] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      fetchClarificationData();
    }
  }, [token]);

  const fetchClarificationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Public endpoint - use centralized API utility
      const result = await api.get(`/api/v1/clarifications/client/${token}`, { showErrorToast: false });
      setData(result);
      
      // Initialize responses with any existing client selections
      if (result.transactions) {
        const initialResponses: Record<string, TransactionResponse> = {};
        result.transactions.forEach((txn: Transaction) => {
          if (txn.client_category) {
            initialResponses[txn.transaction_id] = {
              transaction_id: txn.transaction_id,
              selected_category: txn.client_category,
              notes: txn.client_notes
            };
          }
        });
        setResponses(initialResponses);
      }
      
    } catch (err: any) {
      // Handle specific error cases for better user messaging
      if (err.message?.includes('404') || err.message?.includes('not found')) {
        setError('This link is invalid or has expired. Please contact your representative for a new link.');
      } else if (err.message?.includes('410') || err.message?.includes('expired')) {
        setError('This link has expired. Please contact your representative for a new link.');
      } else {
        setError(err.message || 'Failed to load clarification request');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (transactionId: string, category: string) => {
    setResponses(prev => ({
      ...prev,
      [transactionId]: {
        ...prev[transactionId],
        transaction_id: transactionId,
        selected_category: category
      }
    }));
  };

  const handleNewCategoryName = (transactionId: string, name: string) => {
    setResponses(prev => ({
      ...prev,
      [transactionId]: {
        ...prev[transactionId],
        new_category_name: name
      }
    }));
  };

  const handleNotesChange = (transactionId: string, notes: string) => {
    setResponses(prev => ({
      ...prev,
      [transactionId]: {
        ...prev[transactionId],
        notes
      }
    }));
  };

  const toggleNotes = (transactionId: string) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  };

  // Bulk apply functions
  const openBulkApply = (txn: Transaction, category: string) => {
    if (!data) return;
    
    const similar = findSimilarTransactions(txn, data.transactions);
    // Filter out already-answered ones
    const unansweredSimilar = similar.filter(t => !responses[t.transaction_id]?.selected_category);
    
    if (unansweredSimilar.length === 0) return;
    
    setBulkApplyModal({
      isOpen: true,
      sourceTransaction: txn,
      similarTransactions: unansweredSimilar,
      selectedCategory: category
    });
  };

  const executeBulkApply = () => {
    const { similarTransactions, selectedCategory, sourceTransaction } = bulkApplyModal;
    if (!selectedCategory || similarTransactions.length === 0) return;
    
    const newResponses = { ...responses };
    similarTransactions.forEach(txn => {
      newResponses[txn.transaction_id] = {
        transaction_id: txn.transaction_id,
        selected_category: selectedCategory,
        notes: `Bulk applied from: ${sourceTransaction?.description}`
      };
    });
    
    setResponses(newResponses);
    setBulkApplySuccess(`Applied "${selectedCategory}" to ${similarTransactions.length} similar transactions!`);
    setBulkApplyModal({ isOpen: false, sourceTransaction: null, similarTransactions: [], selectedCategory: '' });
    
    // Clear success message after 3 seconds
    setTimeout(() => setBulkApplySuccess(null), 3000);
  };

  // Compute similar transaction counts
  const similarCounts = useMemo(() => {
    if (!data) return {};
    const counts: Record<string, number> = {};
    data.transactions.forEach(txn => {
      const similar = findSimilarTransactions(txn, data.transactions);
      const unanswered = similar.filter(t => !responses[t.transaction_id]?.selected_category);
      counts[txn.transaction_id] = unanswered.length;
    });
    return counts;
  }, [data, responses]);

  const handleSubmit = async () => {
    if (!token || !data) return;
    
    // Validate all transactions have a response
    const unanswered = data.transactions.filter(
      txn => !responses[txn.transaction_id]?.selected_category
    );
    
    if (unanswered.length > 0) {
      setError(`Please categorize all ${unanswered.length} remaining transaction(s) before submitting.`);
      return;
    }
    
    // Validate "Other" selections have custom names
    const invalidOther = Object.values(responses).filter(
      r => r.selected_category === 'Other' && !r.new_category_name?.trim()
    );
    
    if (invalidOther.length > 0) {
      setError('Please provide a category name for all "Other" selections.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const submission = {
        submissions: Object.values(responses)
      };
      
      // Public endpoint - use centralized API utility
      const result = await api.post(`/api/v1/clarifications/client/${token}/submit`, submission, { showErrorToast: false });
      setSubmitted(true);
      
    } catch (err: any) {
      setError(err.message || 'Failed to submit responses');
    } finally {
      setSubmitting(false);
    }
  };

  const getProgressPercent = () => {
    if (!data) return 0;
    const answered = Object.keys(responses).filter(
      id => responses[id]?.selected_category
    ).length;
    return Math.round((answered / data.transactions.length) * 100);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
          <p className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>Loading your clarification request...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="max-w-md w-full rounded-2xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--color-needs-human-tint)' }}>
            <AlertCircle className="w-8 h-8" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <h1 className="cs-h2 mb-2">Link Error</h1>
          <p className="cs-body mb-6" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="cs-label px-6 py-2 rounded-lg transition-colors"
            style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Already completed state
  if (data?.status === 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="max-w-md w-full rounded-2xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'transparent' }}>
            <CheckCircle2 className="w-8 h-8" style={{ color: 'var(--color-verified)' }} />
          </div>
          <h1 className="cs-h2 mb-2">Already Submitted</h1>
          <p className="cs-body mb-2" style={{ color: 'var(--color-text-secondary)' }}>{data.message}</p>
          {data.completed_at && (
            <p className="cs-meta">
              Completed on {new Date(data.completed_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Success state after submission
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="max-w-md w-full rounded-2xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'transparent' }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: 'var(--color-verified)' }} />
          </div>
          <h1 className="cs-display mb-3">Thank You!</h1>
          <p className="cs-body mb-6" style={{ color: 'var(--color-text-secondary)' }}>
            Your responses have been submitted successfully. A data specialist will review your categorizations shortly.
          </p>
          <div className="rounded-lg p-4 text-left" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
              <div className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>
                <p className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>What happens next?</p>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Our team will incorporate your responses into your claim analysis. You'll be contacted if we need any additional information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main portal view
  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }}>
      {/* Bulk Apply Modal */}
      {bulkApplyModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl max-w-lg w-full p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }} data-testid="bulk-apply-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                <Layers className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              </div>
              <div>
                <h3 className="cs-h3">Apply to Similar Transactions</h3>
                <p className="cs-meta">
                  Found {bulkApplyModal.similarTransactions.length} similar transactions
                </p>
              </div>
            </div>

            <div className="mb-4 p-3 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <p className="cs-meta mb-1">Category to apply:</p>
              <p className="cs-body font-semibold" style={{ color: 'var(--color-text-primary)' }}>{bulkApplyModal.selectedCategory}</p>
            </div>

            <div className="mb-4">
              <p className="cs-body font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Will be applied to:</p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {bulkApplyModal.similarTransactions.map(txn => (
                  <div key={txn.transaction_id} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                    <div className="flex-1 min-w-0">
                      <p className="cs-body font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{txn.description}</p>
                      <p className="cs-meta">{txn.date} • <span className="cs-figure"><FinancialValue value={txn.amount} cents /></span></p>
                    </div>
                    <Check className="w-4 h-4 flex-shrink-0 ml-2" style={{ color: 'var(--color-needs-human-text)' }} />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setBulkApplyModal({ isOpen: false, sourceTransaction: null, similarTransactions: [], selectedCategory: '' })}
                className="cs-body flex-1 px-4 py-2 rounded-lg font-medium transition-colors"
                style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                Cancel
              </button>
              <button
                onClick={executeBulkApply}
                data-testid="confirm-bulk-apply"
                className="cs-body flex-1 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                <Copy className="w-4 h-4" />
                Apply to All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Apply Success Toast */}
      {bulkApplySuccess && (
        <div className="cs-body fixed top-4 right-4 z-50 px-4 py-3 rounded-lg flex items-center gap-2 animate-slide-in" style={{ background: 'var(--color-verified)', color: '#FEFEFE' }}>
          <CheckCircle2 className="w-5 h-5" />
          <span>{bulkApplySuccess}</span>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-10" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="cs-h2">Transaction Review</h1>
              <p className="cs-meta">
                Hi {data?.claimant_name}, please review and categorize these transactions
              </p>
            </div>
            <div className="cs-meta flex items-center gap-2">
              <Shield className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
              <span>Secure</span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="cs-meta">
                {Object.keys(responses).filter(id => responses[id]?.selected_category).length} of {data?.transactions.length || 0} categorized
              </span>
              <span className="cs-meta font-medium" style={{ color: 'var(--color-needs-human-text)' }}>{getProgressPercent()}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${getProgressPercent()}%`, background: 'var(--color-brand)' }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Error alert */}
        {error && (
          <div className="mb-6 rounded-lg p-4 flex items-start gap-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
            <div>
              <p className="cs-body font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Please fix the following:</p>
              <p className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              ×
            </button>
          </div>
        )}

        {/* Instructions */}
        <div className="rounded-xl p-4 mb-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
            <div className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>
              <p className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>How to categorize:</p>
              <ol className="list-decimal ml-4 space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                <li>Review each transaction below</li>
                <li>Select the most appropriate category from the dropdown</li>
                <li>Add any helpful notes (optional)</li>
                <li>Click "Submit All Responses" when done</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Transaction list */}
        <div className="space-y-4" data-testid="transaction-list">
          {data?.transactions.map((txn, index) => {
            const response = responses[txn.transaction_id];
            const isAnswered = !!response?.selected_category;
            const showOtherInput = response?.selected_category === 'Other';
            const showNotes = expandedNotes.has(txn.transaction_id);
            
            return (
              <div
                key={txn.transaction_id}
                data-testid={`transaction-card-${index}`}
                className="rounded-xl transition-all"
                style={{
                  background: 'var(--color-bg-surface)',
                  border: isAnswered
                    ? '2px solid var(--color-verified)'
                    : '2px solid var(--color-border)',
                }}
              >
                <div className="p-5">
                  {/* Transaction header */}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className="cs-chip inline-flex items-center justify-center w-7 h-7 rounded-full"
                          style={
                            isAnswered
                              ? { background: 'transparent', color: 'var(--color-verified)' }
                              : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                          }
                        >
                          {isAnswered ? <Check className="w-4 h-4" /> : index + 1}
                        </span>
                        <span className="cs-meta">{txn.date}</span>
                        <span
                          className="cs-figure font-bold"
                          style={{ color: txn.amount < 0 ? 'var(--color-text-muted)' : 'var(--color-text-primary)' }}
                        >
                          <FinancialValue value={txn.amount} cents />
                        </span>
                      </div>
                      <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>{txn.description}</p>
                      {txn.current_category && (
                        <p className="cs-meta mt-1">
                          Current guess: <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>{txn.current_category}</span>
                        </p>
                      )}
                    </div>
                    {txn.question && (
                      <div className="rounded-lg px-3 py-2 max-w-xs" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                        <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                          <span className="font-medium">Question:</span> {txn.question}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Category selector */}
                  <div className="space-y-3">
                    <label className="cs-label block" style={{ color: 'var(--color-text-secondary)' }}>
                      Select Category <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
                    </label>
                    <div className="relative">
                      <select
                        data-testid={`category-select-${index}`}
                        value={response?.selected_category || ''}
                        onChange={(e) => handleCategorySelect(txn.transaction_id, e.target.value)}
                        className="cs-body w-full px-4 py-3 rounded-lg appearance-none cursor-pointer transition-colors"
                        style={
                          isAnswered
                            ? { border: '2px solid var(--color-verified)', background: 'transparent', color: 'var(--color-text-primary)' }
                            : { border: '2px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                        }
                      >
                        <option value="">-- Select a category --</option>
                        <optgroup label="Income">
                          {data?.categories.filter(c => c.type === 'income').map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Fixed Costs">
                          {data?.categories.filter(c => c.type === 'fixed').map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Variable Costs">
                          {data?.categories.filter(c => c.type === 'variable').map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Other">
                          {data?.categories.filter(c => ['one-time', 'non-operating', 'other'].includes(c.type)).map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </optgroup>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none" style={{ color: 'var(--color-text-dim)' }} />
                    </div>

                    {/* Custom category input for "Other" */}
                    {showOtherInput && (
                      <input
                        type="text"
                        placeholder="Enter custom category name..."
                        data-testid={`custom-category-${index}`}
                        value={response?.new_category_name || ''}
                        onChange={(e) => handleNewCategoryName(txn.transaction_id, e.target.value)}
                        className="cs-body w-full px-4 py-2 rounded-lg focus:outline-none"
                        style={{ border: '2px solid var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)', color: 'var(--color-text-primary)' }}
                      />
                    )}

                    {/* Bulk Apply Button - shows when category selected and similar transactions exist */}
                    {isAnswered && similarCounts[txn.transaction_id] > 0 && response?.selected_category !== 'Other' && (
                      <button
                        type="button"
                        onClick={() => openBulkApply(txn, response.selected_category)}
                        data-testid={`bulk-apply-btn-${index}`}
                        className="cs-body flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors"
                        style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                      >
                        <Layers className="w-4 h-4" />
                        Apply "{response.selected_category}" to {similarCounts[txn.transaction_id]} similar
                      </button>
                    )}

                    {/* Notes toggle and input */}
                    <div>
                      <button
                        type="button"
                        onClick={() => toggleNotes(txn.transaction_id)}
                        className="cs-body flex items-center gap-1"
                        style={{ color: 'var(--color-needs-human-text)' }}
                      >
                        {showNotes ? '− Hide notes' : '+ Add a note (optional)'}
                      </button>
                      {showNotes && (
                        <textarea
                          placeholder="Add any helpful context or notes..."
                          data-testid={`notes-input-${index}`}
                          value={response?.notes || ''}
                          onChange={(e) => handleNotesChange(txn.transaction_id, e.target.value)}
                          rows={2}
                          className="cs-body mt-2 w-full px-4 py-2 rounded-lg focus:outline-none resize-none"
                          style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Submit section */}
        <div className="mt-8 rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>
                {getProgressPercent() === 100
                  ? 'All transactions categorized!'
                  : `${data?.transactions.length || 0 - Object.keys(responses).filter(id => responses[id]?.selected_category).length} transactions remaining`
                }
              </p>
              <p className="cs-meta">
                Your responses help ensure accurate claim calculations
              </p>
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || getProgressPercent() < 100}
              data-testid="submit-button"
              className="cs-body px-8 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all"
              style={
                getProgressPercent() === 100
                  ? { background: 'var(--color-verified)', color: '#FEFEFE' }
                  : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)', cursor: 'not-allowed' }
              }
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit All Responses
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer info */}
        <div className="cs-meta mt-6 text-center">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>Link expires in 30 days</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span>Your data is secure</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
